import React, { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Radio, Video, Mic, Calendar, Clock, Lock, Crown, Pencil } from 'lucide-react';
import { createLive, updateLive } from '../api/lives';
import { useCategories } from '../api/categories';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';
import { useNavigate } from 'react-router-dom';
import { fmtCredits, creditsToEur } from '../lib/currency';
import useCreatorPriceOptions from '../hooks/useCreatorPriceOptions';
import useAppConfig from '../hooks/useAppConfig';
import { LiveDateTimePicker } from './LiveDateTimePicker';
import { useLiveDraft } from '../hooks/useLiveDraft';
import './LiveDialog.css';

const isoLocalNow = (addMinutes = 60) => {
  const d = new Date(Date.now() + addMinutes * 60_000);
  const tz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};

// Convert an ISO/UTC datetime string into the value format expected by
// <input type="datetime-local"> (YYYY-MM-DDTHH:mm) in the user's local TZ.
const isoToLocalInput = (iso) => {
  if (!iso) return isoLocalNow(60);
  const d = new Date(iso);
  if (isNaN(d.getTime())) return isoLocalNow(60);
  const tz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};

const buildFormFromLive = (live) => ({
  title: live?.title || '',
  description: live?.description || '',
  kind: live?.kind === 'audio' ? 'audio' : 'video',
  scheduled_local: isoToLocalInput(live?.scheduled_at),
  duration_minutes: live?.duration_minutes ?? 60,
  is_paid: !!live?.is_paid,
  price: live?.price != null ? String(live.price) : '100',
  category: live?.category || '',
  max_participants: live?.max_participants != null ? String(live.max_participants) : '',
  privacy: live?.privacy === 'private' ? 'private' : 'public',
  video_format: live?.video_format || '16:9',
});

const emptyForm = () => ({
  title: '',
  description: '',
  kind: 'video',
  scheduled_local: isoLocalNow(60),
  duration_minutes: 60,
  is_paid: false,
  price: '100',
  category: '',
  max_participants: '',
  privacy: 'public',
  video_format: '16:9',
});

// Phase 28 — best-effort detection of the user's IANA timezone (e.g. 'Europe/Rome').
const detectTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch { return ''; }
};

const CreateLiveDialog = ({ open, onOpenChange, onCreated, editLive = null, onUpdated }) => {
  const { t } = useTranslation('lives');
  const { toast } = useToast();
  const { user } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const canMonetize = !!user?.is_verified_creator;
  const needsCreatorActivation = !hasActiveCreatorProfile(user);
  const isEdit = !!editLive;
  const [saving, setSaving] = useState(false);
  // Phase 52 · DB-managed price options for `live` scope.
  const { credits: priceCredits } = useCreatorPriceOptions('live');
  // Phase F3 — Voice-First: video kind gated by admin flag. Editing an
  // existing (legacy) video live still shows its own kind.
  const { videoLiveEnabled } = useAppConfig();
  const videoKindAllowed = videoLiveEnabled || (isEdit && editLive?.kind !== 'audio');

  const initialForm = useMemo(() => editLive ? buildFormFromLive(editLive)
    : { ...emptyForm(), kind: videoLiveEnabled ? 'video' : 'audio' }, [open, editLive, videoLiveEnabled]);
  const { form, setForm, clearDraft: reset, hasDraft, storageError } = useLiveDraft({
    open, userId: user?.id, liveId: editLive?.id, initialForm,
  });

  const submit = async () => {
    if (!form.title.trim()) {
      toast({ title: t('create_dialog.title_required'), variant: 'destructive' });
      return;
    }
    // Phase 55 — description and category are mandatory.
    if (!form.description.trim()) {
      toast({ title: 'La descrizione è obbligatoria', variant: 'destructive' });
      return;
    }
    if (!categories.some((c) => c.slug === form.category)) {
      toast({ title: 'Seleziona una categoria', variant: 'destructive' });
      return;
    }
    if (form.is_paid) {
      const p = parseInt(form.price, 10);
      if (!priceCredits.includes(p)) {
        toast({
          title: t('create_dialog.price_invalid_title'),
          description: t('create_dialog.price_invalid_desc', { options: priceCredits.join(', ') }),
          variant: 'destructive',
        });
        return;
      }
    }
    const scheduledDate = new Date(form.scheduled_local);
    if (!Number.isFinite(scheduledDate.getTime())) {
      toast({ title: t('draft.invalid_date'), variant: 'destructive' });
      return;
    }
    const scheduled_at = scheduledDate.toISOString();
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      scheduled_at,
      duration_minutes: parseInt(form.duration_minutes, 10) || 60,
      kind: videoKindAllowed ? form.kind : 'audio',
      is_paid: form.is_paid,
      price: form.is_paid ? parseInt(form.price, 10) : 0,
      category: form.category.trim(),
      max_participants: form.max_participants ? parseInt(form.max_participants, 10) : null,
      privacy: form.privacy,
      video_format: form.video_format || '16:9',
      creator_timezone: detectTimezone(),
    };
    setSaving(true);
    try {
      let finalLive;
      if (isEdit) {
        finalLive = await updateLive(editLive.id, payload);
        toast({ title: t('create_dialog.success_updated'), description: form.title });
        reset();
        onOpenChange(false);
        onUpdated?.(finalLive);
      } else {
        finalLive = await createLive(payload);
        toast({ title: t('create_dialog.success_created'), description: form.title });
        reset();
        onOpenChange(false);
        onCreated?.(finalLive);
      }
    } catch (err) {
      toast({
        title: isEdit ? t('create_dialog.error_update') : t('create_dialog.error_create'),
        description: String(err.response?.data?.detail || '').split('|').pop() || '',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="live-schedule-dialog inset-x-3 bottom-3 top-3 flex h-[calc(100dvh-1.5rem)] w-auto max-w-none max-h-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-lg p-4 data-[state=open]:animate-none data-[state=closed]:animate-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-full sm:max-w-md sm:max-h-[calc(100dvh-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:p-6"
        data-testid={isEdit ? 'edit-live-dialog' : 'create-live-dialog'}>
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? (
              <>
                <Pencil className="h-5 w-5 gold-text" /> {t('create_dialog.title_edit')}
              </>
            ) : (
              <>
                <Radio className="h-5 w-5 text-red-500" /> {t('create_dialog.title')}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('create_dialog.description_edit') : t('create_dialog.description')}
          </DialogDescription>
        </DialogHeader>
        {(hasDraft || storageError) && <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs" data-testid="live-draft-bar">
          <span className={storageError ? 'text-red-500' : 'text-muted-foreground'} data-testid="live-draft-status">
            {t(storageError ? 'draft.storage_unavailable' : 'draft.saved')}
          </span>
          {hasDraft && <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={reset}
            className="h-7 px-2 text-xs" data-testid="live-draft-discard">{t('draft.discard')}</Button>}
        </div>}
        <div className="flex min-h-0 flex-1 touch-pan-y flex-col gap-3 overflow-y-auto overscroll-contain py-1 pr-1" data-testid="live-dialog-scroll-body">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_title')}</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t('create_dialog.field_title_placeholder')}
              maxLength={140}
              data-testid="live-title-input"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_description')}</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="live-description-input"
              maxLength={1000}
              placeholder={t('create_dialog.field_description_placeholder')}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_kind')}</label>
            <div className="flex gap-2">
              {videoKindAllowed && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, kind: 'video' })}
                  data-testid="live-kind-video-btn"
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold ${
                    form.kind === 'video'
                      ? 'border-black bg-black text-white'
                      : 'border-border bg-card text-foreground hover:bg-secondary'
                  }`}
                >
                  <Video className="h-4 w-4" /> {t('create_dialog.field_kind_video')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setForm({ ...form, kind: 'audio' })}
                data-testid="live-kind-audio-btn"
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold ${
                  form.kind === 'audio'
                    ? 'border-black bg-black text-white'
                    : 'border-border bg-card text-foreground hover:bg-secondary'
                }`}
              >
                <Mic className="h-4 w-4" /> {t('create_dialog.field_kind_audio')}
              </button>
            </div>
          </div>
          {form.kind === 'video' && (
            <div data-testid="live-format-block">
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_format')}</label>
              <div className="flex gap-2">
                {[
                  { v: '16:9', labelKey: 'format_horizontal', box: 'w-10 h-5' },
                  { v: '1:1',  labelKey: 'format_square',     box: 'w-7 h-7' },
                  { v: '9:16', labelKey: 'format_vertical',   box: 'w-5 h-8' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, video_format: opt.v })}
                    className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold ${
                      form.video_format === opt.v
                        ? 'border-amber-500 bg-amber-500/10 text-amber-700'
                        : 'border-border bg-card text-foreground hover:bg-secondary'
                    }`}
                    data-testid={`live-format-${opt.v.replace(':', '-')}`}
                  >
                    <div className={`rounded-sm bg-current opacity-40 ${opt.box}`} />
                    {t(`create_dialog.${opt.labelKey}`)}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">{t('create_dialog.format_hint')}</p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-foreground">
                <Calendar className="h-3.5 w-3.5" /> {t('create_dialog.field_datetime')}
              </label>
              <LiveDateTimePicker
                value={form.scheduled_local}
                onChange={(value) => setForm({ ...form, scheduled_local: value })}
              />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-foreground">
                <Clock className="h-3.5 w-3.5" /> {t('create_dialog.field_duration_min')}
              </label>
              <Input
                type="number"
                min="5"
                max="480"
                data-testid="live-duration-input"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_category')}</label>
              <select
                value={categories.some((c) => c.slug === form.category) ? form.category : ''}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="create-live-category"
              >
                <option value="" disabled>{t('create_dialog.field_category_placeholder')}</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_max')}</label>
              <Input
                type="number"
                min="1"
                max="100000"
                placeholder={t('create_dialog.field_max_placeholder')}
                value={form.max_participants}
                onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
                data-testid="create-live-max-participants"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.field_privacy')}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'public',  labelKey: 'vis_public' },
                { v: 'private', labelKey: 'vis_private' },
              ].map((p) => (
                <button
                  type="button"
                  key={p.v}
                  onClick={() => setForm({ ...form, privacy: p.v })}
                  data-testid={`create-live-privacy-${p.v}`}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                    form.privacy === p.v
                      ? 'gold-gradient border-amber-500/60 text-black'
                      : 'border-border bg-secondary text-muted-foreground'
                  }`}
                >
                  {t(`create_dialog.${p.labelKey}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-background p-3" data-testid="live-ticket-section">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground">
              {t('create_dialog.ticket_title')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="live-free-toggle"
                onClick={() => setForm({ ...form, is_paid: false })}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
                  !form.is_paid
                    ? 'border-black bg-black text-white'
                    : 'border-border bg-card text-foreground hover:bg-secondary'
                }`}
              >
                {t('create_dialog.ticket_free')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!canMonetize) {
                    toast({
                      title: needsCreatorActivation
                        ? t('create_dialog.monetize_locked_vip_title')
                        : t('create_dialog.monetize_locked_suspended_title'),
                      description: needsCreatorActivation
                        ? t('create_dialog.monetize_locked_vip_desc')
                        : t('create_dialog.monetize_locked_suspended_desc'),
                    });
                    return;
                  }
                  setForm((current) => ({ ...current, is_paid: true }));
                }}
                aria-disabled={!canMonetize}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
                  !canMonetize
                    ? 'cursor-not-allowed border-border bg-secondary text-muted-foreground'
                    : form.is_paid
                      ? 'border-amber-600 bg-amber-500 text-white'
                      : 'border-border bg-card text-foreground hover:bg-secondary'
                }`}
                data-testid="live-paid-toggle"
              >
                {canMonetize ? t('create_dialog.ticket_paid') : (
                  <span className="inline-flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" /> {t('create_dialog.ticket_paid_vip')}
                  </span>
                )}
              </button>
            </div>
            {!canMonetize && (
              <button
                type="button"
                onClick={() => { onOpenChange(false); navigate(needsCreatorActivation ? '/profile' : '/contact'); }}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold gold-text hover:bg-amber-500/20"
                data-testid="live-paid-locked-cta"
              >
                <Crown className="h-3.5 w-3.5" />
                {needsCreatorActivation
                  ? t('create_dialog.monetize_cta_become_vip')
                  : t('create_dialog.monetize_cta_suspended')}
              </button>
            )}
            {form.is_paid && canMonetize && (
              <div className="mt-3" data-testid="live-price-block">
                <label className="mb-1 block text-xs font-semibold text-foreground">{t('create_dialog.price_label')}</label>
                <select
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="live-price-select"
                >
                  {priceCredits.map((c) => (
                    <option key={c} value={c}>
                      {c === 0 ? 'Gratis · €0.00' : `${fmtCredits(c)} · €${creditsToEur(c).toFixed(2)}`}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  <Trans
                    i18nKey="create_dialog.price_hint"
                    ns="lives"
                    values={{ amount: fmtCredits(form.price) }}
                    components={{ 1: <span className="font-semibold" /> }}
                  />
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t border-border pt-3">
          <Button type="button" variant="ghost" disabled={saving} onClick={() => onOpenChange(false)} data-testid="live-dialog-cancel">
            {t('draft.close')}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className={isEdit ? 'gold-gradient text-black hover:opacity-90' : 'bg-red-500 hover:bg-red-600'}
            data-testid="live-dialog-submit"
          >
            {saving
              ? (isEdit ? t('create_dialog.submitting_edit') : t('create_dialog.submitting'))
              : (isEdit ? t('create_dialog.submit_edit') : t('create_dialog.submit'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLiveDialog;
